import type { ViolationMessage, ViolationMessageDraft, ViolationMessageLimits } from '../../lib/api'

/**
 * Roughly what a collapsed Windows notification shows of the body before its expand chevron, measured
 * on Windows 10 (ADR-S-053). Advice rather than a limit: past it the practice a message cites may be
 * the part nobody reads, so the form says so and saves anyway.
 */
export const VISIBLE_WITHOUT_EXPANDING = 130

/** What the agent puts in place of its own placeholders when the form previews a message. */
export const PREVIEW_SAMPLE = { app: 'claude', rules: 'Customer account numbers', severity: 'high' }

/** Fills in the one placeholder the server owns, exactly as publication does. */
export function expandReference(template: string, reference: string): string {
  return template.split('{reference}').join(reference.trim())
}

/** The notification as somebody would see it, with sample values where the agent fills in its own. */
export function previewNotice(template: string, reference: string): string {
  return expandReference(template, reference)
    .split('{app}')
    .join(PREVIEW_SAMPLE.app)
    .split('{rules}')
    .join(PREVIEW_SAMPLE.rules)
    .split('{severity}')
    .join(PREVIEW_SAMPLE.severity)
}

export interface NoticeMeasure {
  titleLength: number
  messageLength: number
  titleOver: boolean
  messageOver: boolean
  /** Where {rules} starts in what the agent receives, or the whole length if it is absent. */
  practiceEnds: number
  /** Whether the part before the rules would need the person to expand the notification to read. */
  hiddenWhenCollapsed: boolean
}

/**
 * Lengths as the agent measures them: after {reference} is filled in and before its own placeholders
 * are. The server refuses anything over; this only lets the form say so while somebody types.
 */
export function measureNotice(draft: ViolationMessageDraft, limits: ViolationMessageLimits): NoticeMeasure {
  const title = expandReference(draft.title.trim(), draft.reference)
  const message = expandReference(draft.message.trim(), draft.reference)
  const rules = message.indexOf('{rules}')
  const practiceEnds = rules < 0 ? message.length : rules
  return {
    titleLength: title.length,
    messageLength: message.length,
    titleOver: title.length > limits.title,
    messageOver: message.length > limits.message,
    practiceEnds,
    hiddenWhenCollapsed: practiceEnds > VISIBLE_WITHOUT_EXPANDING
  }
}

/** A new message's starting words: the shape the agent's notification shows best. */
export const BLANK_DRAFT: ViolationMessageDraft = {
  name: '',
  reference: '',
  title: '{reference}',
  message: 'This prompt conflicts with {reference}. Remove the sensitive part, or ask IT for an approved tool. Matched: {rules}.'
}

/** A built-in message as the start of one the organization owns, which is how a built-in is changed. */
export function copyOf(message: ViolationMessage, taken: readonly string[]): ViolationMessageDraft {
  const names = new Set(taken.map((name) => name.toLowerCase()))
  let name = `${message.name} (ours)`
  for (let n = 2; names.has(name.toLowerCase()); n++) name = `${message.name} (ours ${n})`
  return { name, reference: message.reference ?? '', title: message.title, message: message.message }
}

export function draftOf(message: ViolationMessage): ViolationMessageDraft {
  return { name: message.name, reference: message.reference ?? '', title: message.title, message: message.message }
}
